/**
 * The questions a plan is built from, as components (B-44).
 *
 * Onboarding asks them once, skippably; the plan flow on Profile asks whichever are still
 * missing, and Profile's own card edits them in place. Three screens, one component each
 * — a second copy of the health question is how the onboarding and plan versions would
 * start disagreeing about what a note is (D-09). What the options ARE still lives in
 * `packages/core/src/onboarding.ts`; this owns how they look.
 */

import type { ConditionId, Health } from "@altrtrack/core";
import { CONDITIONS, MAX_NOTE_CHARS, RED_FLAGS, recordedConditions } from "@altrtrack/core";

/**
 * A list of answers as full-width targets.
 *
 * `selected` takes either the chosen id or an array of them, which is what makes this one
 * component do both the single-answer questions and the multiple-answer one — the only
 * difference between them is whether picking replaces or toggles, and that belongs to the
 * caller.
 */
export function Choices<T extends string | number>({
  options,
  selected,
  onPick,
}: {
  options: readonly { id: T; label: string; blurb?: string }[];
  selected: T | readonly T[] | undefined;
  onPick: (id: T) => void;
}) {
  const chosen = (id: T) => (Array.isArray(selected) ? selected.includes(id) : selected === id);

  return (
    <ul className="choices">
      {options.map((option) => (
        <li key={option.id}>
          <button
            className={`choice${chosen(option.id) ? " chosen" : ""}`}
            type="button"
            aria-pressed={chosen(option.id)}
            onClick={() => onPick(option.id)}
          >
            <span className="choice-label">{option.label}</span>
            {option.blurb && <span className="sub">{option.blurb}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Small toggles in a wrapping row, for a question with many short answers — thirteen
 * conditions, or seven weekdays — where a full-width row each would be a scroll.
 */
export function Chips<T extends string | number>({
  options,
  isOn,
  onToggle,
  label,
}: {
  options: readonly { id: T; label: string }[];
  isOn: (id: T) => boolean;
  onToggle: (id: T) => void;
  label: string;
}) {
  return (
    <div className="chips" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.id}
          className={`chip${isOn(option.id) ? " chosen" : ""}`}
          type="button"
          aria-pressed={isOn(option.id)}
          onClick={() => onToggle(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Injuries and past surgeries.
 *
 * `onChange` is handed an updater rather than a value, for the reason the goals list
 * learned the hard way in B-38: two chips tapped inside one render must both land, and a
 * value computed from the render that drew the button would drop the first.
 *
 * Deselecting the last chip goes back to "not answered" rather than to an empty list —
 * an empty list is not an answer (`isStepAnswered`), and storing one would read as "none"
 * to nothing and as "answered" to nobody.
 */
export function HealthQuestion({
  value,
  onChange,
}: {
  value: Health | undefined;
  onChange: (update: (current: Health | undefined) => Health | undefined) => void;
}) {
  const conditions = recordedConditions(value);
  const selected = new Set(conditions.map((c) => c.id));
  const none = value !== undefined && "none" in value;

  const toggle = (id: ConditionId) =>
    onChange((current) => {
      const list = recordedConditions(current);
      const next = list.some((c) => c.id === id) ? list.filter((c) => c.id !== id) : [...list, { id }];
      return next.length > 0 ? { conditions: next } : undefined;
    });

  const setNote = (id: ConditionId, note: string) =>
    onChange((current) => ({
      conditions: recordedConditions(current).map((c) => (c.id === id ? { id, note } : c)),
    }));

  const groups = [
    { key: "joints", title: "Joints & back" },
    { key: "medical", title: "Medical" },
  ] as const;

  return (
    <div className="health">
      {groups.map((group) => {
        const options = CONDITIONS.filter((c) => c.group === group.key);
        const chosen = conditions.filter((c) => options.some((o) => o.id === c.id));
        return (
          <section className="health-group" key={group.key}>
            <h2 className="label">{group.title}</h2>
            <Chips options={options} isOn={(id) => selected.has(id)} onToggle={toggle} label={group.title} />
            {chosen.map((c) => {
              const label = CONDITIONS.find((o) => o.id === c.id)!.label;
              return (
                <label className="field" key={c.id}>
                  <span className="label">{label} — anything we should know?</span>
                  <input
                    className="input"
                    value={c.note ?? ""}
                    maxLength={MAX_NOTE_CHARS}
                    placeholder="Optional — which side, how long ago"
                    onChange={(e) => setNote(c.id, e.target.value)}
                  />
                </label>
              );
            })}
          </section>
        );
      })}

      {conditions.some((c) => RED_FLAGS.includes(c.id)) && (
        <p className="sub">We'll suggest checking your plan with a doctor.</p>
      )}

      <button
        className={`choice${none ? " chosen" : ""}`}
        type="button"
        aria-pressed={none}
        onClick={() => onChange(() => ({ none: true }))}
      >
        <span className="choice-label">None of these</span>
      </button>
    </div>
  );
}
